import { ChangeEvent, useState } from 'react'

import { Button } from '@it-incubator/ui-kit'
import { Grid, TextField } from '@mui/material'
import { SelectChangeEvent } from '@mui/material/Select'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { SuperCheckbox } from 'common/components/custom-checkbox/SuperCheckbox'
import { CustomModal } from 'common/components/custom-modal/CustomModal'
import { Playlist, PlaylistType, Setting } from 'features/playlists/service'
import moment from 'moment'
import { CourseTypes, useGetCoursesQuery } from 'services/courses'
import { capitalizeFirstLetter } from 'utils'

import { PlaylistSettings } from './playlist-settings/PlaylistSettings'

type Props = {
  callback: (playlistModel: Playlist) => void
  close: () => void
  defName?: string
  defSlug?: string
  defDescription?: string
  defSettings?: Setting[]
  defSeoDescription?: string
  show: boolean
}

export const PlaylistModal = ({
  callback,
  close,
  defName,
  defSettings,
  defSlug,
  defDescription,
  defSeoDescription,
  show,
}: Props) => {
  const [name, setName] = useState(defName || '')
  const [description, setDescription] = useState(defDescription || '')
  const [seoDescription, setSeoDescription] = useState(defSeoDescription || '')
  const [slug, setSlug] = useState(defSlug || '')
  const [settings, setSettings] = useState<Setting[]>(defSettings || [])
  const [error, setError] = useState(false)
  const [errorSlug, setErrorSlug] = useState(false)
  const [errorDescription, setErrorDescription] = useState(false)
  const [errorSeoDescription, setErrorSeoDescription] = useState(false)

  const defStart = moment().unix() * 1000 - 1000 * 60 * 60 * 24 * 366 * 2 // - 2 years
  const defEnd = moment().unix() * 1000 + 1000 * 60 * 60 * 24 * 366 * 2 // + 2 years

  const { data: courses } = useGetCoursesQuery()

  const handleCoursesSelected = (courseId: number) => {
    if (settings.some(selectedCourse => selectedCourse.courseId === courseId)) {
      setSettings(settings.filter(selectedCourse => selectedCourse.courseId !== courseId))

      return
    }

    setSettings([
      ...settings,
      {
        accessLevel: 0,
        courseId: courseId,
        maxStudentStartCourseDate: defEnd,
        minStudentStartCourseDate: defStart,
        plType: '',
      },
    ])
  }

  const changeName = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value
    const maxLengthValue = 250

    if (value.length > maxLengthValue) {
      setError(true)
    } else {
      setName(value)
      setError(false)
    }
  }

  const changeSlug = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value
    const maxLengthValue = 50

    setSlug(value)

    if (value.length > maxLengthValue) {
      setErrorSlug(true)
    } else {
      setErrorSlug(false)
      setSlug(value)
    }
  }

  const changeDescription = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value
    const maxLengthValue = 100

    if (value.length > maxLengthValue) {
      setErrorDescription(true)
    } else {
      setErrorDescription(false)
      setDescription(value)
    }
  }

  const changeSeoDescription = (e: ChangeEvent<HTMLInputElement>) => {
    setSeoDescription(e.currentTarget.value)
  }

  const changeLevelAccess = (event: SelectChangeEvent<number>, courseId: number) => {
    setSettings(
      settings.map(selectedCourse =>
        selectedCourse.courseId === courseId
          ? { ...selectedCourse, accessLevel: +event.target.value }
          : { ...selectedCourse }
      )
    )
  }

  const changeStartDate = (event: null | number, courseId: number) => {
    setSettings(
      settings.map(selectedCourse =>
        selectedCourse.courseId === courseId
          ? { ...selectedCourse, minStudentStartCourseDate: moment(event).unix() * 1000 }
          : { ...selectedCourse }
      )
    )
  }

  const changeEndDate = (event: null | number, courseId: number) => {
    setSettings(
      settings.map(selectedCourse =>
        selectedCourse.courseId === courseId
          ? { ...selectedCourse, maxStudentStartCourseDate: moment(event).unix() * 1000 }
          : { ...selectedCourse }
      )
    )
  }

  const changePlaylistType = (event: SelectChangeEvent<PlaylistType>, courseId: number) => {
    setSettings(
      settings.map(selectedCourse =>
        selectedCourse.courseId === courseId
          ? { ...selectedCourse, plType: event.target.value as PlaylistType }
          : { ...selectedCourse }
      )
    )
  }

  const onSave = () => {
    // Если среди выбранных курсов есть курсы с типом Public, то необходимо указать Slug
    if (
      settings.some(setting => {
        const course = courses?.items.find(course => course.id === setting.courseId)

        return course?.type === CourseTypes.Public
      })
    ) {
      if (!slug) {
        setErrorSlug(true)

        return
      }
    }

    callback({
      name: name,
      settings: settings,
      ...(slug && { slug }),
      ...(description && { description }),
      ...(seoDescription && { seoDescription }),
    })

    if (!defName && !defSettings) {
      setSettings([])
      setName('')
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <CustomModal
        modalTitle={defName && defSettings ? 'Редактировать плейлист' : 'Новый плейлист'}
        onClose={close}
        open={show}
      >
        <Grid marginBottom={2}>
          <TextField
            error={error}
            fullWidth
            helperText={error && 'Максимальная длина 250 символов'}
            label={'Название плейлиста'}
            onChange={changeName}
            value={name}
          />
        </Grid>

        <Grid marginBottom={2}>
          <TextField
            error={errorSlug}
            fullWidth
            helperText={errorSlug && 'Максимальная длина 50 символов'}
            label={'Slug'}
            onChange={changeSlug}
            value={slug}
          />
        </Grid>

        <Grid marginBottom={2}>
          <TextField
            multiline
            error={errorDescription}
            fullWidth
            helperText={errorDescription && 'Максимальная длина 100 символов'}
            label={'Описание плейлиста'}
            onChange={changeDescription}
            value={description}
          />
        </Grid>

        <Grid marginBottom={2}>
          <TextField
            multiline
            error={errorSeoDescription}
            fullWidth
            helperText={errorSeoDescription && 'Максимальная длина 100 символов'}
            label={'SEO описание'}
            onChange={changeSeoDescription}
            value={seoDescription}
          />
        </Grid>

        <h4 style={{ marginBottom: '9px' }}>Доступно для курсов:</h4>

        <Grid marginBottom={2}>
          {courses?.items.map(course => {
            const selected = settings.some(selectedCourse => selectedCourse.courseId === course.id)
            const currentCourse = settings.find(
              selectedCourse => selectedCourse.courseId === course.id
            ) as Setting

            return (
              <div key={course.title + course.id}>
                <SuperCheckbox checked={selected} onChange={() => handleCoursesSelected(course.id)}>
                  {capitalizeFirstLetter(course.title)}
                </SuperCheckbox>

                {selected && (
                  <PlaylistSettings
                    changeEndDate={e => changeEndDate(e, course.id)}
                    changeLevelAccess={e => changeLevelAccess(e, course.id)}
                    changePlaylistType={e => changePlaylistType(e, course.id)}
                    changeStartDate={e => changeStartDate(e, course.id)}
                    endDate={currentCourse.maxStudentStartCourseDate}
                    levelAccess={currentCourse.accessLevel}
                    playlistType={currentCourse.plType}
                    startDate={currentCourse.minStudentStartCourseDate}
                  />
                )}
              </div>
            )
          })}
        </Grid>

        <Grid container justifyContent={'flex-end'} marginTop={4}>
          <Button disabled={!name || error} onClick={onSave}>
            Сохранить
          </Button>
        </Grid>
      </CustomModal>
    </LocalizationProvider>
  )
}
